import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import '../InterviewForm.css'; // Reusing existing styles
import logger from './../utils/logger.js';


const PhoneInterview = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // Default to India
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const navigate = useNavigate();
  const { interview_id: interviewId } = useParams();
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
  
  useEffect(() => {
    // If no interview_id is provided, show an error
    if (!interviewId) {
      setError('No interview ID provided. Please check the URL and try again.');
    }
  }, [interviewId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!interviewId) {
      setError('No interview ID provided. Please check the URL and try again.');
      return;
    }
    
    if (!phoneNumber) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Make the API call
      const fullPhoneNumber = `${countryCode}${phoneNumber}`;
      
      // Configure the request with Authorization header
      const headers = {
        'Content-Type': 'application/json',
        
      };
      
      const response = await axios.post(
        `https://${BASE_URL}/api/interview/start_phone_interview`,
        {
          interview_id: interviewId,
          to_number: fullPhoneNumber
        },
        {
          headers         }
      );      
      
      // Check if the API call was successful
      if (response.status === 200 || response.status === 201) {
        // Navigate to the thank you page
        navigate('/thankyou', { 
          state: { 
            message: 'Your phone interview has been scheduled. You will receive a call shortly.',
            phoneNumber: fullPhoneNumber
          }
        });
      } else {
        setError('Failed to schedule interview. Please try again later.');
      }
    } catch (err) {
      // Handle API error
      logger.error('API Error:', err);
      
      // Check if it's an authorization error
      if (err.response?.status === 401) {
        setShowTokenInput(true);
        setError(err.response?.data?.detail || 'Authentication failed. Please check with your administrator.');
      } else {
        setError(err.response?.data?.message || err.response?.data?.detail || 'An error occurred while scheduling your interview. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Common country codes
  const countryCodes = [
    { code: '+1', country: 'United States' },
    { code: '+44', country: 'United Kingdom' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+86', country: 'China' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+81', country: 'Japan' },
    { code: '+7', country: 'Russia' },
    { code: '+55', country: 'Brazil' },
    { code: '+52', country: 'Mexico' },
    { code: '+27', country: 'South Africa' },
    { code: '+82', country: 'South Korea' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <div className="interview-container-form" style={{ maxWidth: '500px', padding: '30px' }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#333', 
          marginBottom: '20px',
          fontSize: '1.8rem',
          fontWeight: '600'
        }}>Start Phone Interview</h2>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            marginBottom: '20px',
            borderLeft: '4px solid #c62828'
          }}>
            {error}
          </div>
        )}

        <div className="guidelines-form" style={{ marginBottom: '30px' }}>
          <h3 style={{ color: '#2196f3', fontSize: '1.2rem' }}>Interview Guidelines</h3>
          <ul>
            <li>Ensure you're in a quiet environment with good reception</li>
            <li>Have a pen and paper ready to take notes</li>
            <li>Please be available for the next 30-45 minutes</li>
            <li>You will receive a call at the number you provide below</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit}>
          <label htmlFor="phoneNumber" style={{ display: 'block', marginBottom: '8px', fontSize: '16px' }}>
            Enter your phone number:
          </label>
          <div style={{ 
            display: 'flex', 
            marginBottom: '20px', 
            gap: '10px',
            alignItems: 'stretch'
          }}>
            <select 
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#f8f9fa',
                fontSize: '16px',
                minWidth: '100px',
                height: '48px',
                marginTop: '6px'
              }}
            >
              {countryCodes.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.code} ({country.country})
                </option>
              ))}
            </select>
            <input
              type="tel"
              id="phoneNumber"
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
              required
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '16px'
              }}
            />
          </div>
          
          {showTokenInput && (
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="apiToken" style={{ display: 'block', marginBottom: '8px', fontSize: '16px' }}>
                API Token:
              </label>
              <input
                type="text"
                id="apiToken"
                placeholder="Enter your API token"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '16px'
                }}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !phoneNumber || !interviewId}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#5cb85c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: isLoading || !phoneNumber || !interviewId ? 'not-allowed' : 'pointer',
              opacity: isLoading || !phoneNumber || !interviewId ? 0.7 : 1,
              transition: 'all 0.3s ease',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: '600'
            }}
          >
            {isLoading ? (
              <div className="spinner" style={{ 
                border: '3px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                borderTop: '3px solid white',
                width: '20px',
                height: '20px',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : 'Start Phone Interview'}
          </button>
        </form>

        <div style={{ 
          marginTop: '30px', 
          textAlign: 'center', 
          color: '#666',
          fontSize: '14px'
        }}>
          <p>Need help? Contact our support team</p>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '5px',
            marginTop: '8px'
          }}>
            <img
              src="https://img.icons8.com/material-outlined/24/0000FF/email-open.png"
              alt="email icon"
              style={{ width: '16px', height: '16px' }}
            />
            <span>Interview@prompthire.ai</span>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '5px',
            marginTop: '5px'
          }}>
            <img
              src="https://img.icons8.com/material-outlined/24/0000FF/phone.png"
              alt="phone icon"
              style={{ width: '16px', height: '16px' }}
            />
            <span>(+91)79790 23221</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneInterview; 